/**
 * React Query hooks for the agency objective-types editor.
 *
 * `retry: false` on the list query is deliberate: the backend endpoint may
 * not exist yet, and a 404 should degrade to an empty list + visible create
 * form rather than retrying and stalling the UI.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  objectiveTypesService,
  ObjectiveType,
  CreateObjectiveTypeInput,
  UpdateObjectiveTypeInput,
} from "../objectiveTypes.service";

export const objectiveTypeKeys = {
  all: ["objectiveTypes"] as const,
  list: () => [...objectiveTypeKeys.all, "list"] as const,
};

export function useObjectiveTypes() {
  return useQuery({
    queryKey: objectiveTypeKeys.list(),
    queryFn: () => objectiveTypesService.list(),
    retry: false,
  });
}

export function useCreateObjectiveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateObjectiveTypeInput) =>
      objectiveTypesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveTypeKeys.list() });
    },
  });
}

export function useUpdateObjectiveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateObjectiveTypeInput }) =>
      objectiveTypesService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveTypeKeys.list() });
    },
  });
}

export function useArchiveObjectiveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => objectiveTypesService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectiveTypeKeys.list() });
    },
  });
}

export type { ObjectiveType };
